import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { LanguageProvider } from "@/i18n/LanguageContext";

// Suppress the benign "ResizeObserver loop" error so the CRA dev error-overlay
// doesn't blanket the screen (false-positive triggered by table/dialog reflow).
const IGNORED_ERRORS = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
window.addEventListener("error", (e) => {
  if (e.message && IGNORED_ERRORS.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Guard React against browser page-translation (Chrome/Edge/Google Translate wraps
// text nodes in <font> tags it owns; React then throws NotFoundError on
// removeChild/insertBefore during re-render → blank screen). See facebook/react#11538.
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) return child;
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.apply(this, arguments);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the service worker so the app can be installed as a
// home-screen / desktop shortcut (PWA). Safe no-op SW (no caching).
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
